import { Trans, useLingui } from "@lingui/react/macro";
import { OPENAI_COMPATIBLE_PROVIDER_ID, OPENAI_COMPATIBLE_VOICE_PRESETS } from "@rakazo/contracts";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
} from "@rakazo/ui-web";
import { Check, ExternalLink, Eye, EyeOff, Mic, Phone, X } from "lucide-react";
import { useEffect, useId, useState } from "react";
import type { LiveVoiceStatus } from "../../lib/live-voice";
import { loadLiveStatus } from "../../lib/live-voice-rpc";
import { useSpaceRpc } from "../../lib/space-rpc";
import { errorText } from "../../lib/user-error";
import { ConnectionDataDisclosure } from "../ConnectionDataDisclosure";
import { AiDataSharingSettings } from "./AiDataSharingSettings";

export type VoiceSetupKind = "dictation" | "live";

const GROQ = OPENAI_COMPATIBLE_VOICE_PRESETS.find((preset) => preset.id === "groq");
const GROQ_KEYS_URL = "https://console.groq.com/keys";
const GOOGLE_KEYS_URL = "https://aistudio.google.com/app/apikey";

/**
 * The key is the only thing standing between a visible voice button and a working one.
 * The modal keeps the explanation, the link to the provider and the field together, so a
 * person does not have to find Settings → Voice or Settings → Models to start.
 */
export function VoiceSetupDialog({
  kind,
  onClose,
  onSaved,
}: {
  kind: VoiceSetupKind;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const fieldId = useId();
  const [apiKey, setApiKey] = useState("");
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveStatus, setLiveStatus] = useState<LiveVoiceStatus | null | undefined>(
    kind === "live" ? undefined : null,
  );

  useEffect(() => {
    if (kind !== "live") return;
    let active = true;
    void loadLiveStatus()
      .then((next) => {
        if (active) setLiveStatus(next);
      })
      .catch(() => {
        if (active) setLiveStatus(null);
      });
    return () => {
      active = false;
    };
  }, [kind]);

  const liveLoading = kind === "live" && liveStatus === undefined;
  const liveBlocked =
    kind === "live" && liveStatus !== undefined && (liveStatus === null || !liveStatus.enabled);
  const alreadyConnected =
    kind === "live" && liveStatus !== undefined && liveStatus !== null && liveStatus.hasGoogleKey;
  const canSave =
    Boolean(apiKey.trim()) && !saving && !liveLoading && !liveBlocked && !alreadyConnected;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const key = apiKey.trim();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (kind === "dictation") {
        if (!GROQ) throw new Error("The Groq preset is missing");
        await rpc.voice.connect({
          provider: OPENAI_COMPATIBLE_PROVIDER_ID,
          apiKey: key,
          baseUrl: GROQ.baseUrl,
          sttModel: GROQ.sttModel,
          transcribeOnly: true,
        });
      } else {
        await rpc.models.connect({
          provider: "google",
          apiKey: key,
          label: "Google",
          // Live voice pays for itself; it must not make Google the bot's chat model.
          makeDefault: false,
        });
      }
      setApiKey("");
      setSaved(true);

      onSaved?.();
    } catch (cause) {
      setError(errorText(cause, t`Could not save the key`));
    } finally {
      setSaving(false);
    }
  }

  const icon =
    kind === "dictation" ? (
      <Mic size={19} strokeWidth={1.8} />
    ) : (
      <Phone size={19} strokeWidth={1.8} />
    );
  const title = kind === "dictation" ? t`Set up voice input` : t`Set up live conversation`;
  const description =
    kind === "dictation"
      ? t`Dictate in any chat. HrokBot transcribes your speech with Groq Whisper.`
      : t`Talk to your bot in real time. Audio is streamed to Google Gemini Live.`;

  return (
    <Dialog
      open
      onOpenChange={(open, details) => {
        if (open) return;
        if (saving) {
          details.cancel();
          return;
        }
        onClose();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="w-[min(460px,calc(100%-2rem))] gap-0 rounded-2xl p-0 sm:max-w-[460px]"
      >
        <div className="flex items-start gap-3 px-5 pt-5">
          <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-muted text-foreground/80">
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-[17px] leading-6 font-semibold text-foreground">
              {title}
            </DialogTitle>
            <p className="mt-1 text-[13px] leading-5 text-muted-foreground">{description}</p>
          </div>
          <DialogClose
            aria-label={t`Close`}
            render={<Button variant="ghost" size="icon-sm" className="-me-1 -mt-1 rounded-full" />}
          >
            <X size={16} strokeWidth={2} />
          </DialogClose>
        </div>

        <div className="p-5 pt-4">
          {saved ? (
            <div className="flex flex-col items-center py-2 text-center">
              <div className="grid size-12 place-items-center rounded-full bg-success/10 text-success">
                <Check size={22} strokeWidth={2} />
              </div>
              <p className="mt-3 text-base font-medium text-foreground">
                {error ? <Trans>Key saved</Trans> : <Trans>Connected</Trans>}
              </p>
              <p className="mt-1 max-w-[34ch] text-[13px] leading-5 text-muted-foreground">
                {kind === "dictation" ? (
                  <Trans>Tap the microphone and speak.</Trans>
                ) : (
                  <Trans>Tap the phone icon to start talking.</Trans>
                )}
              </p>
              <AiDataSharingSettings />
              <Button className="mt-4 h-10 w-full rounded-xl" onClick={onClose}>
                <Trans>Done</Trans>
              </Button>
            </div>
          ) : liveLoading ? (
            <p className="py-6 text-center text-[13px] text-muted-foreground">
              <Trans>Checking live voice…</Trans>
            </p>
          ) : liveBlocked ? (
            <div className="py-2">
              <p className="text-[13px] leading-5 text-muted-foreground">
                {liveStatus === null ? (
                  <Trans>Live conversation isn't available on this server.</Trans>
                ) : (
                  <Trans>Live conversation is turned off on this server.</Trans>
                )}
              </p>
              <Button variant="outline" className="mt-4 h-10 w-full rounded-xl" onClick={onClose}>
                <Trans>Close</Trans>
              </Button>
            </div>
          ) : alreadyConnected ? (
            <div className="flex flex-col items-center py-2 text-center">
              <div className="grid size-12 place-items-center rounded-full bg-success/10 text-success">
                <Check size={22} strokeWidth={2} />
              </div>
              <p className="mt-3 text-base font-medium text-foreground">
                <Trans>Google key connected</Trans>
              </p>
              <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                <Trans>Tap the phone icon to start talking.</Trans>
              </p>
              <AiDataSharingSettings />
              <Button className="mt-4 h-10 w-full rounded-xl" onClick={onClose}>
                <Trans>Done</Trans>
              </Button>
            </div>
          ) : (
            <form onSubmit={save} className="space-y-4">
              <div className="rounded-xl border border-border bg-muted/40 p-3">
                <p className="text-[13px] leading-5 text-muted-foreground">
                  {kind === "dictation" ? (
                    <Trans>Open Groq Console, create an API key, and paste it below.</Trans>
                  ) : (
                    <Trans>Open Google AI Studio, create an API key, and paste it below.</Trans>
                  )}
                </p>
                <a
                  href={kind === "dictation" ? GROQ_KEYS_URL : GOOGLE_KEYS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {kind === "dictation" ? (
                    <Trans>Open Groq Console</Trans>
                  ) : (
                    <Trans>Open Google AI Studio</Trans>
                  )}
                  <ExternalLink size={13} strokeWidth={1.8} />
                </a>
              </div>

              <Field>
                <FieldLabel htmlFor={fieldId}>
                  {kind === "dictation" ? (
                    <Trans>Groq API key</Trans>
                  ) : (
                    <Trans>Google API key</Trans>
                  )}
                </FieldLabel>
                <div className="relative mt-1.5">
                  <Input
                    id={fieldId}
                    type={visible ? "text" : "password"}
                    value={apiKey}
                    onChange={(event) => {
                      setApiKey(event.target.value);
                      setError(null);
                    }}
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    placeholder={kind === "dictation" ? "gsk_…" : "AIza…"}
                    className="h-11 rounded-xl pr-11 font-mono text-[13px]"
                  />
                  <button
                    type="button"
                    aria-label={visible ? t`Hide key` : t`Show key`}
                    onClick={() => setVisible((current) => !current)}
                    className="absolute inset-y-0 right-0 grid w-10 place-items-center text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {visible ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </Field>

              <p className="text-[12px] leading-[1.5] text-muted-foreground">
                {kind === "dictation" ? (
                  <Trans>
                    The key is stored encrypted on your account and used only for dictation.
                  </Trans>
                ) : (
                  <Trans>
                    The key is stored encrypted on your account. Google bills live audio to it.
                  </Trans>
                )}
              </p>
              <p className="text-[12px] leading-[1.5] text-muted-foreground">
                <Trans>
                  Allow microphone access when your system asks. If you blocked it, enable it in
                  system or browser settings.
                </Trans>
              </p>

              <ConnectionDataDisclosure
                provider={kind === "dictation" ? "Groq" : "Google"}
                use={kind === "dictation" ? "voice" : "live"}
              />

              <Button type="submit" className="h-11 w-full rounded-xl" disabled={!canSave}>
                {saving ? <Trans>Saving…</Trans> : <Trans>Save key</Trans>}
              </Button>
            </form>
          )}
          {error ? (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
